import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActionArea, TextField, InputAdornment, List, ListItemButton, ListItemText, Divider, Chip, Stack, Button, Link as MuiLink } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';

const SECTIONS: Array<{ key: string; title: string; items: Array<{ title: string; description?: string; href?: string }>; }> = [
  {
    key: 'getting-started',
    title: 'Pierwsze kroki',
    items: [
      { title: 'Logowanie i konto', description: 'Problemy z logowaniem, profil i wylogowanie.' },
      { title: 'Nawigacja po aplikacji', description: 'Gdzie znaleźć projekty, oferty i bazę danych.' },
      { title: 'Skróty klawiaturowe', description: 'Przyspiesz pracę dzięki skrótom.' },
    ],
  },
  {
    key: 'offers',
    title: 'Oferty',
    items: [
      { title: 'Tworzenie oferty', description: 'Dodawanie pozycji, terminów i załączników.' },
      { title: 'Statusy ofert', description: 'Czym różnią się statusy i kiedy się zmieniają.' },
      { title: 'Powiadomienia o terminach', description: 'Jak działają przypomnienia i integracja z kalendarzem.' },
    ],
  },
  {
    key: 'projects',
    title: 'Projekty',
    items: [
      { title: 'Zakładanie projektu', description: 'Tworzenie projektu oraz podstawowe dane.' },
      { title: 'Harmonogram', description: 'Zadania, zależności i postęp realizacji.' },
      { title: 'Finanse projektu', description: 'Budżet, koszty i przepływy pieniężne.' },
    ],
  },
  {
    key: 'troubleshooting',
    title: 'Rozwiązywanie problemów',
    items: [
      { title: 'Brak dostępu do modułu', description: 'Uprawnienia i kontakt z administratorem.' },
      { title: 'Błąd zapisu formularza', description: 'Najczęstsze przyczyny i jak je wyeliminować.' },
      { title: 'Wolne działanie', description: 'Diagnoza połączenia i zgłoszenie problemu.' },
    ],
  },
];

const QUICK_LINKS: Array<{ icon: React.ElementType; title: string; href?: string; color: string }>[] = [
  [
    { icon: ArticleOutlinedIcon, title: 'Dokumentacja modułów', href: '/docs', color: '#1976d2' },
    { icon: VideoLibraryOutlinedIcon, title: 'Wideo i tutoriale', href: undefined, color: '#9c27b0' },
  ],
  [
    { icon: SupportAgentOutlinedIcon, title: 'Skontaktuj się z nami', href: undefined, color: '#2e7d32' },
    { icon: LightbulbOutlinedIcon, title: 'Nowości i roadmapa', href: undefined, color: '#ef6c00' },
  ],
];

const HelpPage: React.FC = () => {
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map(s => ({
      ...s,
      items: s.items.filter(i => i.title.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q))
    })).filter(s => s.items.length > 0);
  }, [query]);

  return (
    <Box>
      {/* HERO */}
      <Card sx={{ mb: 2, overflow: 'hidden' }}>
        <CardContent sx={{ py: 4 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center' }}>
                <HelpOutlineOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Centrum pomocy</Typography>
                <Typography variant="body2" color="text.secondary">Znajdź odpowiedzi, przewodniki i wskazówki do Unitresco</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Wyszukaj problem lub temat"
              size="small"
              fullWidth
              InputProps={{ startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )}}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* QUICK LINKS */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        {QUICK_LINKS.flat().map((q, idx) => {
          const Icon = q.icon;
          return (
            <Grid key={idx} item xs={12} sm={6} md={3}>
              <Card>
                <CardActionArea onClick={() => q.href ? (window.location.href = q.href) : undefined}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ bgcolor: q.color, color: '#fff', width: 36, height: 36, borderRadius: 1, display: 'grid', placeItems: 'center' }}>
                      <Icon />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.title}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* CONTENT */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {filtered.map((section, sIdx) => (
            <Card key={section.key} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">{section.title}</Typography>
                <List>
                  {section.items.map((it, iIdx) => (
                    <ListItemButton key={`${section.key}-${iIdx}`} onClick={() => it.href ? (window.location.href = it.href) : undefined}>
                      <ListItemText
                        primary={<Typography sx={{ fontWeight: 600 }}>{it.title}</Typography>}
                        secondary={it.description}
                      />
                      <ArrowForwardIosOutlinedIcon fontSize="small" color="action" />
                    </ListItemButton>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <Card>
              <CardContent>
                <Typography>Brak wyników dla: “{query}”. Spróbuj innego zapytania.</Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Najczęstsze tematy</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {['Oferty', 'Harmonogram', 'Kalendarz', 'Uprawnienia', 'Integracje'].map((t) => (
                  <Chip key={t} label={t} size="small" onClick={() => setQuery(t)} />
                ))}
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Wsparcie</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Nie znalazłeś odpowiedzi? Napisz do nas: <MuiLink href="mailto:support@unitresco.pl">support@unitresco.pl</MuiLink>
              </Typography>
              <Button sx={{ mt: 1.5 }} variant="contained" onClick={() => window.dispatchEvent(new CustomEvent('ui:shortcuts:open'))}>Skróty klawiaturowe</Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HelpPage;


